var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DEFAULT_LOCALE, LEGACY_LINKS, LINKS } from '../constants';
import { Environment } from '../enums';
import { getDomainConfig } from '../routes';
import { clearLoginReferer, getLoginReferer, setLoginReferer } from './clientStorage';
import { getEnvironment, isCSR, isRaisinComDomain } from './domain';
import { getFeatureFlagClient } from './featureFlag';
/**
 * window.location.origin   → 'https://www.example-domain.com:8080'
 .protocol → 'https:'
 .host     → 'www.example-domain.com:8080'
 .hostname → 'www.example-domain.com'
 .port     → '8080'
 .pathname → '/order/'
 .search   → '?filter=123'
 .hash     → '#2'
 .href     → 'https://www.example-domain.com:8080/order/?filter=123#2'
 * @returns The relative href value of the current `window.location` object
 */
export const getRelativeHref = () => isCSR() ? window.location.href.replace(window.location.origin, '') : '';
export const redirect = (url) => { var _a; return isCSR() && ((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(url)); };
export const goToRegistrationPage = () => redirect(LINKS.REGISTRATION);
export const getLocaleBasePath = (locale) => {
    if (!locale || locale === 'undefined')
        return '';
    const config = getDomainConfig(locale);
    return (config === null || config === void 0 ? void 0 : config.basePath) || '';
};
export const getLocaleOriginPath = () => {
    var _a;
    const localeBasePath = getLocaleBasePath(document.documentElement.lang);
    return `${(_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.origin}${localeBasePath}`;
};
export const getRelativePathWithLocalePrefix = ({ path, fallbackLocale = 'de-de', }) => {
    const locale = isCSR() ? document.documentElement.lang : fallbackLocale;
    const localeBasePath = getLocaleBasePath(locale).replace('/', '');
    const basePathSeparator = path.startsWith('/') ? '' : '/';
    return localeBasePath !== '' ? `${localeBasePath}${basePathSeparator}${path}` : `${path}`;
};
export const goTo = (path, router) => {
    var _a;
    const relativePath = getRelativePathWithLocalePrefix({ path });
    if (router) {
        router.push(relativePath);
        return;
    }
    (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(relativePath);
};
export const goToLoginPage = ({ refererRequired } = { refererRequired: true }) => {
    if (!isCSR())
        return;
    if (refererRequired) {
        setLoginReferer();
    }
    goTo(LINKS.LOGIN);
};
export const isPensionLogoutNeeded = () => {
    const environment = getEnvironment();
    return environment === Environment.Production && window.location.host.endsWith('weltsparen.de');
};
export const goToDeProdPensionLogoutPage = () => {
    // invalidates pension token raisid for Germany Prod environment, after it will redirect to login page
    window === null || window === void 0 ? void 0 : window.location.assign(LINKS.WELTSPAREN_DE_PENSION_LOGOUT);
};
/**
 * @deprecated this will be replaced by the verifyDashboardPageUrlAndRedirect function soon
 */
export const goToDashboardPage = () => {
    var _a, _b;
    const localeBasePath = getLocaleBasePath(document.documentElement.lang);
    if (isRaisinComDomain() && localeBasePath !== '') {
        (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(`${localeBasePath}${LEGACY_LINKS.DASHBOARD}`);
    }
    else {
        (_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.assign(LEGACY_LINKS.DASHBOARD);
    }
};
export const visitDashboard = (b2cUseNewDashboard, shouldReturnFullUrl = false) => {
    var _a;
    const locale = isCSR() ? document.documentElement.lang : DEFAULT_LOCALE;
    const localeBasePath = getLocaleBasePath(locale);
    const dashboardPath = b2cUseNewDashboard ? LINKS.DASHBOARD.DEFAULT : LEGACY_LINKS.DASHBOARD;
    const fullUrl = `${localeBasePath}${dashboardPath}`;
    return shouldReturnFullUrl ? fullUrl : (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(fullUrl);
};
export const verifyDashboardPageUrlAndRedirect = (shouldReturnFullUrl = false) => __awaiter(void 0, void 0, void 0, function* () {
    const featureFlags = yield getFeatureFlagClient();
    const b2cUseNewDashboard = featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.variation('b2cUseNewDashboard', false);
    return visitDashboard(b2cUseNewDashboard, shouldReturnFullUrl);
});
export const goToRefererOrDashboard = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // referer already includes locale. e.g) /pl-pl/user-account
    const referer = getLoginReferer();
    if (referer) {
        clearLoginReferer();
        (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(referer);
    }
    else {
        yield verifyDashboardPageUrlAndRedirect();
    }
});
export const goToDepositsProductPage = (productId) => {
    const productPageUrl = `/deposits/opening/overview/?productId=${productId}`;
    goTo(productPageUrl);
};
/**
 * @deprecated this will be replaced by the goToRefererOrDashboard function soon
 */
export const goToRefererOrDefault = () => {
    var _a;
    // referer already includes locale. e.g) /pl-pl/user-account
    const referer = getLoginReferer();
    if (referer) {
        clearLoginReferer();
        (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.assign(referer);
    }
    else {
        goToDashboardPage();
    }
};
/**
 * Safari browser prevents opening new tabs by default
 *
 * This is a workaround to open new tab in Safari bypassing this limitation
 *
 * @see https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
 * @param href The link to be opened in a new tab
 * @returns void
 */
export const openLinkInNewTab = (href) => {
    if (!href)
        return;
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.setAttribute('style', 'display: none');
    a.setAttribute('href', href);
    a.setAttribute('target', '_blank');
    a.click();
    document.body.removeChild(a);
};
//# sourceMappingURL=navigation.js.map